import React, { useState, useEffect } from 'react';
import Button from '../../components/common/button/Button';
import Modal from '../../components/common/model/Modal';
import styles from './EmployeeList.module.scss';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import employeeService from '../../services/employeeService';
import InputField from '../../components/common/inputField/InputField';
import { Employee } from '../../types/employeeTypes';

const EmployeeList: React.FC<{ role?: string }> = ({ role = 'admin' }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'employee',
    is_active: true
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState<string | null>(null);

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getEmployees();
      const normalizedEmployees = Array.isArray(response)
        ? response
            .map(emp => {
              if (!emp || typeof emp !== 'object') {
                console.warn('Invalid employee object:', emp);
                return null;
              }

              return {
                id: emp.id || '',
                name: emp.name || '',
                email: emp.email || '',
                phone_number: emp.phone_number || '',
                role: emp.role || 'employee',
                is_active: emp.is_active !== undefined ? emp.is_active : true,
                created_at: emp.created_at || new Date().toISOString(),
                updated_at: emp.updated_at || new Date().toISOString(),
              };
            })
            .filter((emp): emp is Employee => emp !== null)
        : [];

      setEmployees(normalizedEmployees);
      console.log("Normalized employees:", normalizedEmployees);
    } catch (err) {
      console.error('Error fetching employees:', err);
      toast.error('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleAdd = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone Number.');
      return;
    }
    
    const data = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      role: form.role === "admin" ? "admin" : "employee",
      is_active: form.is_active
    };

    try {
      await employeeService.createEmployee(data); 
      toast.success('Employee created successfully!');
      setShowModal(false);
      resetForm();
      fetchEmployees(); 
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create employee';
      toast.error(message);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await employeeService.deleteEmployee(id);
      toast.success('Employee deleted successfully!');
      fetchEmployees();
    } catch (err) {
      toast.error('Failed to delete employee');
    }
  };

  const handleToggleActive = async (id: string) => {
    const currentEmployee = employees.find(emp => emp.id === id);
    if (!currentEmployee) {
      toast.error('Employee not found');
      return;
    }

    const currentStatus = currentEmployee.is_active ?? true;
    const newStatus = !currentStatus;
    
    setEmployees(employees.map(emp =>
      emp.id === id ? { ...emp, is_active: newStatus } : emp
    ));

    try {
      const updatedEmployee = await employeeService.toggleEmployeeStatus(id);
      
      if (updatedEmployee && typeof updatedEmployee === 'object') {
        setEmployees(prevEmployees => prevEmployees.map(emp => {
          if (emp.id === id) {
            return {
              ...emp,
              ...updatedEmployee,
              is_active: updatedEmployee.is_active ?? newStatus,
            };
          }
          return emp;
        }));
      }
      
      toast.success('Employee status updated successfully!');
    } catch (err) {
      setEmployees(prevEmployees => prevEmployees.map(emp =>
        emp.id === id ? { ...emp, is_active: currentStatus } : emp
      ));
      
      const message = err instanceof Error ? err.message : 'Failed to update employee status';
      toast.error(message);
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      is_active: true,
      role: 'employee'
    });
  }

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleOpenEditModal = (employee: Employee) => {
    setForm({
      name: employee.name || '',
      email: employee.email || '',
      phone: employee.phone_number || '',
      is_active: employee.is_active ?? true,
      role: employee.role || 'employee',
    });
    setEditingEmployeeId(employee.id);
    setEditMode(true);
    setShowModal(true);
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error('Please fill in all required fields: Name, Email, and Phone Number.');
      return;
    }
    if (!editingEmployeeId) return;
    
    const data = {
      id: editingEmployeeId,
      name: form.name,
      email: form.email,
      phone: form.phone,
      is_active: form.is_active,
      role: form.role,
    };
    
    try {
      await employeeService.updateEmployee(data);
      toast.success('Employee updated successfully!');
      setShowModal(false);
      setEditMode(false);
      setEditingEmployeeId(null);
      resetForm();
      fetchEmployees();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update employee';
      toast.error(message);
    }
  };

  const filteredEmployees = employees.filter(emp =>
    (emp.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (emp.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (emp.role?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      <h4>Employees</h4>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.searchContainer}>
            <div className={styles.searchWrapper}>
              <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search employees by name, email, or role..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearButton}
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className={styles.headerRight}>
          {role === 'admin' && (
            <Button variant="primary" onClick={handleOpenModal}>
              + Add Employee
            </Button>
          )}
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Role</th>
              {role === 'admin' && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={role === 'admin' ? 5 : 4} style={{ textAlign: 'center', padding: '20px' }}>
                  Loading employees...
                </td>
              </tr>
            ) : (
              Array.isArray(filteredEmployees) && filteredEmployees.length > 0 ? (
                filteredEmployees.map(emp => {
                  if (!emp || !emp.id) {
                    console.warn('Invalid employee in render:', emp);
                    return null;
                  }
                  
                  const isActive = emp.is_active !== undefined ? emp.is_active : true;
                  
                  return (
                    <tr key={emp.id} className={!isActive ? styles.inactive : ''}>
                      <td>{emp.name || '-'}</td>
                      <td>{emp.email || '-'}</td>
                      <td>{emp.phone_number || '-'}</td>
                      <td>{emp.role === "admin" ? "Admin" : "Employee"}</td>
                      {role === 'admin' && (
                        <td>
                          <div className={styles.actionIcons}>
                            <Button
                              variant={isActive ? 'secondary' : 'success'}
                              className={styles.actionButton}
                              onClick={() => handleToggleActive(emp.id)}
                              aria-label={isActive ? 'Disable Employee' : 'Enable Employee'}
                            >
                              {isActive ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="7" r="4"></circle>
                                  <path d="M5 21v-2a4 4 0 0 1 4-4h2"></path>
                                  <polyline points="16 19 18 21 22 17"></polyline>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="7" r="4"></circle>
                                  <path d="M5 21v-2a4 4 0 0 1 4-4h2"></path>
                                  <line x1="17" y1="17" x2="21" y2="21"></line>
                                  <line x1="21" y1="17" x2="17" y2="21"></line>
                                </svg>
                              )}
                            </Button>
                            <Button
                              onClick={() => handleOpenEditModal(emp)}
                              className={styles.actionButton}
                              aria-label="Edit Employee"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9"/>
                                <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19.5 3 21l1.5-4L16.5 3.5z"/>
                              </svg>
                            </Button>
                            <Button
                              onClick={() => handleDelete(emp.id)}
                              className={styles.actionButton}
                              aria-label="Delete Employee"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                }).filter(row => row !== null)
              ) : (
                <tr>
                  <td colSpan={role === 'super_admin' ? 5 : 4} style={{ textAlign: 'center', padding: '20px' }}>
                    {searchTerm ? 'No employees found matching your search' : 'No employees found'}
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      
      <Modal 
        isOpen={showModal} 
        onClose={() => { 
          setShowModal(false); 
          setEditMode(false); 
          setEditingEmployeeId(null); 
          resetForm(); 
        }} 
        title={editMode ? "Edit Employee" : "Add Employee"} 
        size='lg'
      >
        <div className={styles.modalForm}>
          <InputField
            label="Name"
            name="name"
            type="text"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder="Employee Name"
            required
          />
          <InputField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="Employee Email"
            required
          />
          <InputField
            label="Phone Number"
            name="phone"
            type="tel"
            value={form.phone}
            onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone Number"
            required
          />
          <label>Role</label>
          <div className={styles.selectField}>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
              className={styles.selectInput}
            >
              <option value="employee">Employee</option>
              <option value="admin">Admin</option>
            </select>
            <span className={styles.selectArrow}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 8 10 12 14 8" />
              </svg>
            </span>
          </div>

          <div className={styles.modalActions}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowModal(false);
                setEditMode(false);
                setEditingEmployeeId(null);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={editMode ? handleSaveEdit : handleAdd}
              className={styles.submitButton}
            >
              {editMode ? 'Save Changes' : 'Add Employee'}
            </Button>
          </div>
        </div>
      </Modal>
      
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default EmployeeList;